export interface PageSuggestion {
  labelKey: string;
  query: string;
}

export interface PageContext {
  pageKey: string;
  introKey: string;
  suggestions: PageSuggestion[];
}

const PAGE_CONTEXTS: { match: (path: string) => boolean; context: PageContext }[] = [
  {
    match: (p) => p === "/" || p === "/home",
    context: {
      pageKey: "home",
      introKey: "chatbot.page_intros.home",
      suggestions: [
        { labelKey: "chatbot.page_suggestions.home.what_can_i_do", query: "What can I do in Refabric?" },
        { labelKey: "chatbot.page_suggestions.home.recent_projects", query: "Show me my recent projects" },
        { labelKey: "chatbot.page_suggestions.home.start_design", query: "How do I start a new design?" },
      ],
    },
  },
  {
    // Design Canvas. Highest-traffic feature in the product and it had no entry, so it fell
    // through to DEFAULT_CONTEXT and offered chips for other features ("How do I create a
    // tech pack?") to a user already standing in the canvas. It is also the one page that
    // publishes a rich page_state snapshot (see CanvasPageStateReporter), so the suggestions
    // can safely assume the assistant knows what is on the board.
    match: (p) => p.startsWith("/design-canvas"),
    context: {
      pageKey: "design_canvas",
      introKey: "chatbot.page_intros.design_canvas",
      suggestions: [
        { labelKey: "chatbot.page_suggestions.design_canvas.generate", query: "How do I generate a design from a prompt?" },
        { labelKey: "chatbot.page_suggestions.design_canvas.reference", query: "How do I use a sketch or fabric as a reference?" },
        { labelKey: "chatbot.page_suggestions.design_canvas.edit", query: "How do I edit a design I already generated?" },
      ],
    },
  },
  {
    // `/report/:id` is the trend report viewer — same subject area, so it reuses this context
    // rather than earning its own. Checked before brand_dna below, but `/brand-dna-report`
    // does not start with `/report`, so there is no shadowing.
    match: (p) => p.startsWith("/moodboards") || p.startsWith("/trends") || p.startsWith("/trend-analysis") || p.startsWith("/discover") || p.startsWith("/report"),
    context: {
      pageKey: "trends",
      introKey: "chatbot.page_intros.trends",
      suggestions: [
        { labelKey: "chatbot.page_suggestions.trends.ss26", query: "Find trends for SS26" },
        { labelKey: "chatbot.page_suggestions.trends.color_palette", query: "What color palettes are trending now?" },
        { labelKey: "chatbot.page_suggestions.trends.silhouettes", query: "Which silhouettes are gaining traction?" },
      ],
    },
  },
  {
    match: (p) => p.startsWith("/brand-dnas") || p.startsWith("/brand-dna-report") || p.startsWith("/fabric-dna"),
    context: {
      pageKey: "brand_dna",
      introKey: "chatbot.page_intros.brand_dna",
      suggestions: [
        { labelKey: "chatbot.page_suggestions.brand_dna.create", query: "How do I create a brand DNA?" },
        { labelKey: "chatbot.page_suggestions.brand_dna.use", query: "How do I use a brand DNA in my designs?" },
      ],
    },
  },
  {
    // `/projects` is the All Projects page — same "find and organise my work" intent.
    match: (p) => p.startsWith("/canvas-projects") || p.startsWith("/projects"),
    context: {
      pageKey: "projects",
      introKey: "chatbot.page_intros.projects",
      suggestions: [
        { labelKey: "chatbot.page_suggestions.projects.new", query: "How do I start a new canvas project?" },
        { labelKey: "chatbot.page_suggestions.projects.organize", query: "How can I organize my designs?" },
      ],
    },
  },
  {
    // ⚠️ MUST STAY ABOVE the photoshoots entry below. `startsWith("/photoshoot")` matches
    // `/photoshoot-assets/*` too, so under the old ordering someone browsing their Models library
    // was greeted with "You're in Photoshoots — which kind of shoot are you planning?" and offered
    // Ghost / Model Change / Product-to-On-Model chips. Reproduced on dev before this change.
    //
    // The matcher was `startsWith("/assets")`, which matched nothing a user can actually stand on:
    // every `/assets*` path is a redirect (`Router.tsx` → `/design-assets/fabric`,
    // `/photoshoot-assets/products`, `/history`). The live routes are `PATHS.DESIGN_ASSETS` and
    // `PATHS.PHOTOSHOOT_ASSETS`. So this whole context was dead and `/design-assets/fabric` fell
    // through to DEFAULT_CONTEXT — offering "How do I create a tech pack?" to someone standing in
    // their fabric library. `/assets` is kept purely as defensive compatibility; those redirects are
    // bare `<Navigate>` routes outside `Layout` (`Router.tsx:318`), so the chatbot is not even
    // mounted while one is resolving and the prefix can never actually be hit.
    //
    // `/history` is the SAME `AssetsPage` component, registered in the same route array as the two
    // libraries (`router/config/index.ts`), and `MyAssetsPage` resolves it to the **Created** tab of
    // that view. Missing it was inconsistent in a self-inflicted way: `/assets/created` redirects to
    // `/history`, so the matcher covered the legacy redirect while dropping its real destination.
    // `/integrations` is deliberately NOT here — it renders `IntegrationsPage`, a different page.
    //
    // `/colors` is `DESIGN_ASSETS_COLOR_PALETTES`, a Design Assets page that exists on `dev` but not
    // on this branch (REF-6361). Listed because THIS matcher makes no claim that the route exists —
    // it is inert wherever it does not — and omitting it would land a live asset page on
    // DEFAULT_CONTEXT the moment this merges to dev, which is the exact defect being fixed here.
    //
    // Both libraries share one context deliberately: the intro already says "products, designs,
    // fabrics, and references", i.e. it was written for both. Per-section chips would need new keys
    // in every locale and are a separate improvement, not part of this defect.
    match: (p) => p.startsWith("/design-assets") || p.startsWith("/photoshoot-assets") || p.startsWith("/history") || p.startsWith("/colors") || p.startsWith("/assets"),
    context: {
      pageKey: "assets",
      introKey: "chatbot.page_intros.assets",
      suggestions: [
        { labelKey: "chatbot.page_suggestions.assets.upload", query: "How do I upload new assets?" },
        { labelKey: "chatbot.page_suggestions.assets.use", query: "How do I use my assets in designs?" },
      ],
    },
  },
  {
    // Bulk Process is a Photoshoot Studio entry point (docs/features/photoshoot-studio.md).
    match: (p) => p.startsWith("/photoshoots") || p.startsWith("/photoshoot") || p.startsWith("/bulk-process"),
    context: {
      pageKey: "photoshoot",
      introKey: "chatbot.page_intros.photoshoot",
      suggestions: [
        { labelKey: "chatbot.page_suggestions.photoshoot.ghost", query: "How does the Ghost shoot work?" },
        { labelKey: "chatbot.page_suggestions.photoshoot.mannequin", query: "When should I use Mannequin shoots?" },
        { labelKey: "chatbot.page_suggestions.photoshoot.tryon", query: "How do I run a Try-On photoshoot?" },
      ],
    },
  },
  {
    match: (p) => p.startsWith("/tech-pack") || p.startsWith("/tech-packs"),
    context: {
      pageKey: "techpack",
      introKey: "chatbot.page_intros.techpack",
      suggestions: [
        { labelKey: "chatbot.page_suggestions.techpack.create", query: "How do I create a tech pack?" },
        { labelKey: "chatbot.page_suggestions.techpack.edit", query: "How do I edit measurements and BOM?" },
        { labelKey: "chatbot.page_suggestions.techpack.export", query: "How do I export a tech pack to Excel?" },
      ],
    },
  },
  {
    match: (p) => p.startsWith("/svg-editor"),
    context: {
      pageKey: "svg_editor",
      introKey: "chatbot.page_intros.svg_editor",
      suggestions: [
        { labelKey: "chatbot.page_suggestions.svg_editor.save", query: "How do I save my edits in the SVG editor?" },
        { labelKey: "chatbot.page_suggestions.svg_editor.download", query: "How do I download my edited drawing?" },
        { labelKey: "chatbot.page_suggestions.svg_editor.tools", query: "What can I edit in the vector editor?" },
      ],
    },
  },
  {
    // `/new-training/{style,fabric,model}` are the live training pages — only the old
    // `/new-training` landing was retired (REF-5739), so this prefix is still correct.
    // `/model/:id` is the trained-model detail page, same subject area.
    match: (p) => p.startsWith("/new-training") || p.startsWith("/model"),
    context: {
      pageKey: "training",
      introKey: "chatbot.page_intros.training",
      suggestions: [
        { labelKey: "chatbot.page_suggestions.training.style", query: "How do I train a style model?" },
        { labelKey: "chatbot.page_suggestions.training.fabric", query: "How do I train a fabric model?" },
        { labelKey: "chatbot.page_suggestions.training.model", query: "How do I train a 3D body model?" },
      ],
    },
  },
  {
    // `/payment/success` and `/payment/error` land here after checkout, where the questions
    // are billing ones ("did it go through", "where are my credits").
    match: (p) => p.startsWith("/pricing") || p.startsWith("/cancel-plan") || p.startsWith("/payment"),
    context: {
      pageKey: "pricing",
      introKey: "chatbot.page_intros.pricing",
      suggestions: [
        { labelKey: "chatbot.page_suggestions.pricing.compare", query: "What's the difference between the plans?" },
        { labelKey: "chatbot.page_suggestions.pricing.credits", query: "How do credits work?" },
      ],
    },
  },
  {
    // Academy. REF-5998 was a user being told twice that Academy does not exist, so a user
    // standing inside it getting suggestions for other features is the same failure in a
    // milder form. Covers /academy, /academy/all-courses, /academy/course/:id[/watch].
    match: (p) => p.startsWith("/academy"),
    context: {
      pageKey: "academy",
      introKey: "chatbot.page_intros.academy",
      suggestions: [
        { labelKey: "chatbot.page_suggestions.academy.what_is", query: "What is Refabric Academy?" },
        { labelKey: "chatbot.page_suggestions.academy.certificate", query: "How do I get a certificate?" },
        { labelKey: "chatbot.page_suggestions.academy.where_start", query: "Which course should I start with?" },
      ],
    },
  },
  {
    match: (p) => p.startsWith("/collection-range-plans"),
    context: {
      pageKey: "collection_range_plan",
      introKey: "chatbot.page_intros.collection_range_plan",
      suggestions: [
        { labelKey: "chatbot.page_suggestions.collection_range_plan.create", query: "How do I create a collection range plan?" },
        { labelKey: "chatbot.page_suggestions.collection_range_plan.use", query: "What can I do with the collection once it's generated?" },
      ],
    },
  },
  {
    match: (p) => p.startsWith("/account"),
    context: {
      pageKey: "account",
      introKey: "chatbot.page_intros.account",
      suggestions: [
        { labelKey: "chatbot.page_suggestions.account.team", query: "How do I invite team members?" },
        { labelKey: "chatbot.page_suggestions.account.billing", query: "Where can I see my billing history?" },
      ],
    },
  },
];

const DEFAULT_CONTEXT: PageContext = {
  pageKey: "default",
  introKey: "chatbot.page_intros.default",
  suggestions: [
    { labelKey: "chatbot.page_suggestions.default.tech_pack", query: "How do I create a tech pack?" },
    { labelKey: "chatbot.page_suggestions.default.photoshoot", query: "Generate a virtual photoshoot" },
    { labelKey: "chatbot.page_suggestions.default.training", query: "Train a style model" },
    { labelKey: "chatbot.page_suggestions.default.trends", query: "Find trends for SS26" },
  ],
};

export const getPageContext = (pathname: string): PageContext => PAGE_CONTEXTS.find((entry) => entry.match(pathname))?.context ?? DEFAULT_CONTEXT;
